/**
 * Version Manager for MobyNet
 * Handles app versioning and cache invalidation to ensure users always run latest code
 *
 * Three version dimensions:
 *   APP_VERSION      — UI/feature version, shown to users
 *   DATA_VERSION     — Dexie schema + OPFS structure version (bump triggers data migration)
 *   PROTOCOL_VERSION — Binary wire protocol version (signal, chunk, RaptorQ params)
 *   BUILD_TIMESTAMP  — Replaced by build script; skipped when unreplaced ('__BUILD_TIME__')
 */

// Version should be updated with each new feature deployment
export const APP_VERSION = '1.0.19.04-alpha';
// Data structure version — bump when Dexie schema or OPFS layout changes
// This is independent of APP_VERSION so UI-only releases don't trigger data migration
export const DATA_VERSION = 1;
// Binary protocol version — peers compare on HELLO to detect incompatibility
// Bump when chunk format, Merkle tree structure, or RaptorQ params change
export const PROTOCOL_VERSION = 1;
// Build timestamp - will be replaced by build script with actual timestamp
export const BUILD_TIMESTAMP = '__BUILD_TIME__';

// Storage keys
const VERSION_KEY = 'mobynet_app_version';
const BUILD_KEY = 'mobynet_build_time';
const DATA_VERSION_KEY = 'mobynet_data_version';
const UPDATE_DISMISSED_KEY = 'mobynet_update_dismissed';

// Prefixes for localStorage keys that must survive cache clears
// All user data, preferences, permissions, and OAuth state use these prefixes
const PRESERVE_PREFIXES = ['moby_', 'moby-', 'mobynet_'];

export class VersionManager {
  private static instance: VersionManager;
  
  private constructor() {
    this.checkVersion();
    this.checkDataVersion();
  }
  
  static getInstance(): VersionManager {
    if (!this.instance) {
      this.instance = new VersionManager();
    }
    return this.instance;
  }
  
  /**
   * Check if app version has changed and handle updates
   */
  private checkVersion(): void {
    const storedVersion = localStorage.getItem(VERSION_KEY);
    const storedBuild = localStorage.getItem(BUILD_KEY);
    
    if (!storedVersion || !storedBuild) {
      // First time user
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      localStorage.setItem(BUILD_KEY, BUILD_TIMESTAMP);
      return;
    }
    
    // Check version — skip BUILD_TIMESTAMP comparison when build script hasn't replaced it
    const buildTimestampValid = BUILD_TIMESTAMP !== '__BUILD_TIME__';
    const versionChanged = storedVersion !== APP_VERSION;
    const buildChanged = buildTimestampValid && storedBuild !== BUILD_TIMESTAMP;
    
    if (versionChanged || buildChanged) {
      this.handleVersionMismatch(storedVersion, storedBuild);
    }
  }
  
  /**
   * Handle version mismatch - clear cache and notify user
   */
  private handleVersionMismatch(oldVersion: string, oldBuild: string): void {
    console.log(`[VERSION] Update detected: ${oldVersion} (${oldBuild}) → ${APP_VERSION} (${BUILD_TIMESTAMP})`);
    
    // Always clear cache and update stored version on mismatch
    this.clearAppCache();
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    localStorage.setItem(BUILD_KEY, BUILD_TIMESTAMP);
    
  }
  
  /**
   * Check if data structure version has changed (Dexie schema / OPFS layout)
   * Runs independently of APP_VERSION so UI-only releases don't trigger migration
   */
  private checkDataVersion(): void {
    const storedDataVersion = localStorage.getItem(DATA_VERSION_KEY);
    const currentDataVersion = String(DATA_VERSION);
    
    if (!storedDataVersion) {
      // First time — store current version
      localStorage.setItem(DATA_VERSION_KEY, currentDataVersion);
      return;
    }
    
    if (storedDataVersion !== currentDataVersion) {
      console.log(`[VERSION] Data version changed: ${storedDataVersion} → ${currentDataVersion}`);
      this.handleDataVersionMigration(Number(storedDataVersion), DATA_VERSION);
      localStorage.setItem(DATA_VERSION_KEY, currentDataVersion);
    }
  }
  
  /**
   * Handle data version migration — prune stale OPFS data
   * Dexie handles its own schema migrations via version().stores().upgrade(),
   * but OPFS has no built-in migration mechanism.
   */
  private handleDataVersionMigration(oldVersion: number, newVersion: number): void {
    console.log(`[VERSION] Running data migration: v${oldVersion} → v${newVersion}`);
    
    // Prune stale OPFS Merkle trees and chunked files from previous data layout
    // Future migrations can add version-specific logic here:
    //   if (oldVersion < 2) { /* migration from v1 to v2 */ }
    this.pruneStaleOPFS().catch(err => {
      console.warn('[VERSION] OPFS pruning failed (non-fatal):', err);
    });
  }
  
  /**
   * Prune stale OPFS data that may be incompatible with current data version.
   * Removes Merkle tree files and incomplete transfer chunks.
   * Complete downloaded files are preserved — they're content-addressed by hash.
   */
  private async pruneStaleOPFS(): Promise<void> {
    try {
      const root = await navigator.storage.getDirectory();
      const stalePatterns = ['merkle-', 'transfer-', 'chunk-'];
      let pruned = 0;
      
      // @ts-ignore — TypeScript doesn't have full OPFS iterator types yet
      for await (const [name, handle] of root.entries()) {
        const isStale = stalePatterns.some(p => name.startsWith(p));
        if (isStale) {
          try {
            await root.removeEntry(name, { recursive: true });
            pruned++;
          } catch {
            // Entry may be locked by another tab — skip
          }
        }
      }
      
      if (pruned > 0) {
        console.log(`[VERSION] Pruned ${pruned} stale OPFS entries`);
      }
    } catch (err) {
      // OPFS may not be available in all contexts
      console.debug('[VERSION] OPFS pruning skipped:', err);
    }
  }
  
  /**
   * Clear application cache while preserving user data.
   * Uses prefix-based preservation: any localStorage key starting with
   * moby_ / moby- / mobynet_ is kept. This is future-proof — new features
   * using these prefixes are automatically preserved without code changes.
   */
  private clearAppCache(): void {
    // Collect all keys that match preserve prefixes
    const preserved: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && PRESERVE_PREFIXES.some(p => key.startsWith(p))) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          preserved[key] = value;
        }
      }
    }
    
    // Clear all localStorage
    localStorage.clear();
    
    // Restore preserved data
    for (const [key, value] of Object.entries(preserved)) {
      localStorage.setItem(key, value);
    }
    
    // Clear session storage (except dismissed updates)
    const dismissedUpdate = sessionStorage.getItem(UPDATE_DISMISSED_KEY);
    sessionStorage.clear();
    if (dismissedUpdate) {
      sessionStorage.setItem(UPDATE_DISMISSED_KEY, dismissedUpdate);
    }
    
    // Service worker lifecycle: post CLEAR_CACHE to active controller,
    // then trigger update check so stale SW itself gets replaced
    if ('serviceWorker' in navigator) {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CACHE',
          version: APP_VERSION
        });
      }
      // Force SW update check — if the SW itself is the stale component,
      // the postMessage above won't reach it, but this will install the new one
      navigator.serviceWorker.getRegistration().then(reg => {
        if (reg) {
          reg.update().catch(() => {});
        }
      }).catch(() => {});
    }
  }
  
  /**
   * Called when the signaling server broadcasts a VERSION message.
   * Compares the server's version against the running bundle and prompts
   * the user to reload if they differ. Fully event-driven — no polling.
   */
  onVersionSignal(serverVersion: string, _serverBuildTimestamp: string): void {
    // Compare APP_VERSION only. The server runs from source so its
    // BUILD_TIMESTAMP is always '__BUILD_TIME__' (unreplaced), while the
    // client's is a real number after build.ts post-processing.
    // BUILD_TIMESTAMP comparison is handled separately by checkVersion() on page load.
    if (serverVersion === APP_VERSION) {
      return;
    }
    
    // Check if user already dismissed this specific update
    const dismissedVersion = sessionStorage.getItem(UPDATE_DISMISSED_KEY);
    if (dismissedVersion === serverVersion) {
      return;
    }
    
    console.log(`[VERSION] Server broadcast: ${serverVersion} — running: ${APP_VERSION} (${BUILD_TIMESTAMP})`);
    this.promptForReload(serverVersion);
  }
  
  /**
   * Prompt user to reload for new version
   */
  private promptForReload(newVersion: string): void {
    // Prevent duplicate notifications
    const existing = document.getElementById('moby-version-notification');
    if (existing) return;
    
    // Inject pulse animation if not already present
    if (!document.getElementById('moby-version-style')) {
      const style = document.createElement('style');
      style.id = 'moby-version-style';
      style.textContent = `
        @keyframes moby-pulse {
          0%, 100% { box-shadow: 0 4px 24px rgba(0,0,0,0.3); }
          50% { box-shadow: 0 4px 32px rgba(0,0,0,0.5), 0 0 0 4px rgba(255,255,255,0.15); }
        }
      `;
      document.head.appendChild(style);
    }
    
    const notification = document.createElement('div');
    notification.id = 'moby-version-notification';
    notification.className = 'fixed top-4 left-1/2 -translate-x-1/2 bg-primary text-on-primary px-6 py-4 rounded-xl z-50';
    notification.style.cssText = 'animation: moby-pulse 2s ease-in-out 3; min-width: 340px;';
    notification.innerHTML = `
      <div class="flex items-center gap-4">
        <div class="text-2xl">&#x21BB;</div>
        <div class="flex-1">
          <div class="font-semibold text-base">New version available</div>
          <div class="text-sm opacity-80">v${newVersion} is ready</div>
        </div>
        <button style="background:#fff;color:#0d1117;padding:8px 16px;border-radius:8px;font-weight:600;font-size:14px;border:none;cursor:pointer;" onmouseover="this.style.background='#e0e0e0'" onmouseout="this.style.background='#fff'" onclick="window.location.reload()">
          Reload Now
        </button>
        <button style="background:transparent;color:inherit;opacity:0.7;padding:4px 8px;border-radius:4px;font-size:12px;border:none;cursor:pointer;" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'" onclick="this.closest('#moby-version-notification').remove(); sessionStorage.setItem('${UPDATE_DISMISSED_KEY}', '${newVersion}')">
          Dismiss
        </button>
      </div>
    `;
    
    document.body.appendChild(notification);
  }
  
  /**
   * Cleanup (no-op — no listeners or intervals to clear in event-driven mode)
   */
  cleanup(): void {
    // Reserved for future use
  }
  
  /**
   * Force reload with cache bypass
   */
  static forceReload(): void {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    // Hard reload
    window.location.reload();
  }
  
  /**
   * Get current app version
   */
  static getVersion(): string {
    return APP_VERSION;
  }
  
  /**
   * Get build timestamp
   */
  static getBuildTime(): string {
    return BUILD_TIMESTAMP;
  }
}

// Auto-initialize on import
if (typeof window !== 'undefined') {
  VersionManager.getInstance();
}
